import { Avatar, Badge, Button, Flex, Input, Select, Spin, Tag, notification } from 'antd';
import './MultiChatContactsPanel.css';
import { useContext, useEffect, useState } from 'react';
import SubscriptionChatsContext, { SubscriptionChatsFilter } from '../../../contexts/data/SubscriptionChatsContext';
import {
  CheckCircleFilled,
  ClockCircleFilled,
  PauseCircleFilled,
  QuestionCircleFilled,
  RobotOutlined,
  UserOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { getTagColor } from '../../../../core/utils/commonUtils';
import { getInterestLevel } from '@leedpulse-monorepo/utils';
import { ContextStatus } from '@leedpulse-monorepo/enums';

interface MultiChatContactsPanelProps {
  onContactSelect: () => void;
}

const MultiChatContactsPanel: React.FC<MultiChatContactsPanelProps> = ({
  onContactSelect,
}) => {
  const subscriptionChatsContext = useContext(SubscriptionChatsContext);

  const [searchInput, setSearchInput] = useState<string>('');

  useEffect(() => {
    if (subscriptionChatsContext.noSearchResults) {
      notification.info({
        message: 'Aucun résultat',
        description: 'Aucun contact ne correspond à votre recherche',
      });
    }
  }, [subscriptionChatsContext.noSearchResults]);

  return (
    <div className="multi-chat-contacts-panel">
      <div className="multi-chat-contacts-panel__controls">
        <Input.Search
          placeholder="Adresse email ou n° de téléphone"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            if (e.target.value === '') {
              subscriptionChatsContext.resetSearch();
            }
          }}
          onSearch={() => subscriptionChatsContext.search(searchInput)}
          disabled={
            subscriptionChatsContext.contactsFilteringStatus ===
            ContextStatus.LOADING
          }
          loading={
            subscriptionChatsContext.contactsFilteringStatus ===
            ContextStatus.LOADING
          }
        />
        <Select<SubscriptionChatsFilter[]>
          mode='multiple'
          allowClear
          maxTagCount={'responsive'}
          placeholder="Filtrer"
          onChange={(value, filters) => {
            subscriptionChatsContext.onFilterChange(searchInput, filters as SubscriptionChatsFilter[]);
          }}
          value={subscriptionChatsContext.constrainedByFilter}
          disabled={
            subscriptionChatsContext.contactsFilteringStatus ===
            ContextStatus.LOADING
          }
          loading={
            subscriptionChatsContext.contactsFilteringStatus ===
            ContextStatus.LOADING
          }
          options={
            [
              {
                label: <Flex align="center"><CheckCircleFilled style={{ marginRight: 6, color: 'green' }} /> Intéressé(s)</Flex>,
                value: 'interested',
                type: 'qualification_ai'
              },
              {
                label: <Flex align="center"><PauseCircleFilled style={{ marginRight: 6, color: 'black' }} /> Inactif(s)</Flex>,
                value: 'not_interested',
                type: 'qualification_ai'
              },
              {
                label: <Flex align="center"><QuestionCircleFilled style={{ marginRight: 6, color: 'purple' }} /> En réflexion</Flex>,
                value: 'considering',
                type: 'qualification_ai'
              },
              {
                label: <Flex align="center"><ClockCircleFilled style={{ marginRight: 6, color: 'orange' }} /> Sans réponse</Flex>,
                value: 'not_responsive',
                type: 'qualification_ai'
              },
              ...subscriptionChatsContext.subscriptionScenarios.map((subscriptionScenario) => ({
                label: subscriptionScenario.name,
                value: subscriptionScenario.id,
                type: 'prospect_category'
              })),
              ...subscriptionChatsContext.subscriptionOwners.map((owner) => ({
                label: (owner.firstname ?? '') + ' ' + (owner.lastname ?? ''),
                value: owner.uid,
                type: 'owner'
              })),
            ]
          }
        />
      </div>
      <div className="multi-chat-contacts-panel__contacts-list">
        {subscriptionChatsContext.contactsFilteringStatus ===
          ContextStatus.LOADING && (
            <div className="contacts-list__loading">
              <Spin />
            </div>
          )}
        {subscriptionChatsContext.contacts.length === 0 && (
          <div className="contacts-list__empty">
            <UserOutlined
              style={{
                fontSize: '2em',
              }}
            />
            <span>Aucun contact</span>
          </div>
        )}
        {subscriptionChatsContext.contacts.map((contact) => (
          <div
            key={contact.id}
            className={classNames({
              'multi-chat-contacts-panel__contacts-list__contact-tile': true,
              'contact-tile--selected':
                subscriptionChatsContext.selectedContact?.id === contact.id,
            })}
            onClick={() => {
              if (subscriptionChatsContext.selectedContact?.id !== contact.id) {
                subscriptionChatsContext.selectContact(contact);
              }
              onContactSelect();
            }}
          >
            <Avatar>
              {contact.contact.firstname[0] +
                contact.contact.lastname[0]}
            </Avatar>
            <div className="contact-tile__content">
              <span className="contact-tile__content__contact-name">
                {contact.contact.firstname +
                  ' ' +
                  contact.contact.lastname}
              </span>
              <span className="contact-tile__content__owner">
                {(subscriptionChatsContext.subscriptionOwners.find(
                  (owner) => owner.uid === contact.contact.owner
                )?.firstname ?? '') +
                  ' ' +
                  (subscriptionChatsContext.subscriptionOwners.find(
                    (owner) => owner.uid === contact.contact.owner
                  )?.lastname ?? '')}
              </span>
              <Tag className='contact-tile__content__owner-tag' color={getTagColor(contact?.qualification_ai?.qualification)}>
                &#8226; {getInterestLevel(contact?.qualification_ai?.qualification || "not_available")}
              </Tag>
            </div>
            <div className="contact-tile__unread-stats">
              <div className="contact-tile__unread-stats__badge">
                <RobotOutlined />
                <Badge
                  count={
                    contact.messages_counters.backoffice?.assistant?.unread ?? 0
                  }
                  style={{
                    backgroundColor:
                      (contact.messages_counters.backoffice?.assistant?.unread ?? 0) > 0
                        ? 'var(--danger-color)'
                        : 'var(--border-color)',
                    fontWeight: 'bold',
                  }}
                  showZero
                />
              </div>
              <div className="contact-tile__unread-stats__badge">
                <UserOutlined />
                <Badge
                  count={contact.messages_counters.backoffice?.user?.unread ?? 0}
                  style={{
                    backgroundColor:
                      (contact.messages_counters.backoffice?.user?.unread ?? 0) > 0
                        ? 'var(--danger-color)'
                        : 'var(--border-color)',
                    fontWeight: 'bold',
                  }}
                  showZero
                />
              </div>
            </div>
          </div>
        ))}
        {subscriptionChatsContext.contactsCount >
          subscriptionChatsContext.contacts.length && (
            <div className="contacts-list__more-button-container">
              <Button
                style={{ width: '100%' }}
                onClick={() => {
                  subscriptionChatsContext.search(searchInput, subscriptionChatsContext.nbPagesLoaded + 1);
                }}
                loading={
                  subscriptionChatsContext.contactsFetchingStatus ===
                  ContextStatus.LOADING
                }
              >
                Charger suivants
              </Button>
            </div>
          )}
      </div>
    </div>
  );
};

export default MultiChatContactsPanel;
